import { useState } from "react";
import "./LovePaid.css";

// =====================================================
// LOVE LEADERBOARD — PAID ENTRY DETAIL CARD
//
// The certificate the user gets for their ₹12: branding, what they submitted,
// when it was approved, when it expires, and whether it is still live.
// Used in two places (the Add Your Name page right after payment, and the Live
// Leaderboard for the owner) so both show exactly the same card.
//
// PRINT  — the browser's own print, no library. A body class switches on the
//          print rules in LovePaid.css, which leave only this card visible; it
//          is removed again on `afterprint` (with a timer as the fallback for
//          browsers that never fire it), so the page is untouched afterwards.
// CLOSE  — hides this card ONLY. The caller persists that preference; the
//          entry, the name on the leaderboard and the 24 hours are unaffected.
// =====================================================

const DATE_OPTS = {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString(undefined, DATE_OPTS);
};

// Whole hours + minutes left, from the server's expiry date.
const formatRemaining = (expiresAt) => {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (!expiresAt || Number.isNaN(ms) || ms <= 0) return null;
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  return `${hours}h ${minutes}m remaining`;
};

function LovePaidCard({ entry, onClose, closing = false }) {
  const [printing, setPrinting] = useState(false);

  if (!entry) return null;

  const isLove = entry.type === "love";
  const remaining = formatRemaining(entry.expiresAt);

  // "Your Name ❤️ Friend/Lover Name". Entries paid for before the second name
  // existed have none, and still show exactly as they always did.
  const partnerName = (entry.partnerName || "").trim();

  const handlePrint = () => {
    const cleanup = () => {
      document.body.classList.remove("lpn-printing");
      window.removeEventListener("afterprint", cleanup);
      setPrinting(false);
    };

    setPrinting(true);
    document.body.classList.add("lpn-printing");
    window.addEventListener("afterprint", cleanup);
    // Safety net: some mobile browsers never fire `afterprint`, and the page
    // must not be left in the print-only state.
    setTimeout(cleanup, 4000);

    window.print();
  };

  return (
    <div className="lpn-card">
      {/* Branding */}
      <div className="lpn-card-brand">
        <img src="/logo192.png" alt="TheBlogsSphere" className="lpn-card-logo" />
        <div>
          <div className="lpn-card-brand-name">TheBlogsSphere</div>
          <span className="lpn-card-brand-sub">Love Leaderboard · Featured Entry</span>
        </div>
      </div>

      {/* What the user submitted — the two people side by side, each with
          their OWN optional photo directly under their own name. A person
          without a photo simply has none; there is never a broken image box. */}
      <h2 className="lpn-card-name">
        <span className="lpn-pair-person">
          <span className="lpn-pair-name">{entry.name}</span>
          {entry.profileImage && (
            <img
              src={entry.profileImage}
              alt=""
              className="lpn-pair-photo"
              loading="lazy"
              decoding="async"
            />
          )}
        </span>

        {partnerName && (
          <>
            <span className="lpn-pair-icon" aria-hidden="true">{isLove ? "❤️" : "🤝"}</span>
            <span className="lpn-pair-person">
              <span className="lpn-pair-name">{partnerName}</span>
              {entry.partnerImage && (
                <img
                  src={entry.partnerImage}
                  alt=""
                  className="lpn-pair-photo"
                  loading="lazy"
                  decoding="async"
                />
              )}
            </span>
          </>
        )}
      </h2>

      <span className={`lpn-card-pill ${isLove ? "lpn-pill-love" : "lpn-pill-friend"}`}>
        {isLove ? "❤️ Love" : "🤝 Friend"}
      </span>

      {/* City the entry plays for. Entries created before the City Pride
          feature carry none and render exactly as they always did. */}
      {entry.city && (
        <div className="lpn-card-city">
          <span aria-hidden="true">📍</span>
          {/* City only — one main city per state, so "Bengaluru" already says
              Karnataka. */}
          <span>{entry.city}</span>
        </div>
      )}

      <p className="lpn-card-content">“{entry.content}”</p>

      {/* Dates — both come from the server, never from a browser clock */}
      <div className="lpn-card-meta">
        <div className="lpn-meta-row">
          <span className="lpn-meta-key">Approved on</span>
          <span className="lpn-meta-val">{formatDate(entry.approvedAt)}</span>
        </div>
        <div className="lpn-meta-row">
          <span className="lpn-meta-key">Expires on</span>
          <span className="lpn-meta-val">{formatDate(entry.expiresAt)}</span>
        </div>
      </div>

      <div className={`lpn-status ${remaining ? "" : "lpn-status-expired"}`}>
        {remaining ? (
          <>
            <span>🟢</span>
            <span>Visible for 24 hours · {remaining}</span>
          </>
        ) : (
          <>
            <span>⚪</span>
            <span>24-hour visibility completed</span>
          </>
        )}
      </div>

      <div className="lpn-card-actions lpn-no-print">
        <button type="button" className="lpn-btn lpn-btn-print" onClick={handlePrint} disabled={printing}>
          🖨️ Print
        </button>
        <button type="button" className="lpn-btn lpn-btn-close" onClick={onClose} disabled={closing}>
          {closing ? "Closing…" : "✕ Close"}
        </button>
      </div>
    </div>
  );
}

export default LovePaidCard;
